import { SortingState } from "@tanstack/react-table"

import getObjectCardsViewConfigStorageKey from "@utils/getObjectCardsViewConfigStorageKey"

import useTable from "@organisms/Table/Table.context"

import { CardsViewContext } from "./CardsView.context"
import { CardsViewContextValues, CardsViewProviderProps } from "./CardsView.types"
import useCardsViewConfig from "./hooks/useCardsViewConfig"

// Cards view machinery only (the attributes and sort fields the object offers, and the persisted cards
// view settings). The object list itself comes from the surrounding Table.Provider.
export default function CardsViewProvider(props: CardsViewProviderProps) {
    const { children, attributes, sortOptions } = props

    const { objectType, objectTypeVariation, activePhase, activeTab, sorting, onSortingChange } = useTable()

    const storageKey = getObjectCardsViewConfigStorageKey({
        objectType,
        objectTypeVariation,
        phase: activePhase,
        tab: activeTab?.title ?? "",
    })

    const { currentCardsViewConfig, updateCardsViewConfig } = useCardsViewConfig(storageKey)

    const attributeVisibility = currentCardsViewConfig?.attributeVisibility ?? {}
    const displayedAttributes = attributes
        .filter((attribute) => attributeVisibility[attribute.value])
        .map((attribute) => attribute.value)

    const toggleAttribute = (attributeValue: string) => {
        updateCardsViewConfig("attributeVisibility", {
            ...attributeVisibility,
            [attributeValue]: !attributeVisibility[attributeValue],
        })
    }

    // The surrounding list resolves its ordering from this view's saved config while the cards view is
    // active, so sort changes go through the list rather than straight into the config.
    const activeSorting = sorting[0]
    const activeSortOption = sortOptions.find((sortOption) => sortOption.value === activeSorting?.id)

    const setSorting = (nextSorting: SortingState) => {
        onSortingChange(nextSorting)
    }

    const setSortField = (fieldValue: string) => {
        setSorting([{ id: fieldValue, desc: activeSorting?.desc ?? false }])
    }

    const setCurrentSortToAscending = () => {
        if (activeSorting) {
            setSorting([{ id: activeSorting.id, desc: false }])
        }
    }

    const setCurrentSortToDescending = () => {
        if (activeSorting) {
            setSorting([{ id: activeSorting.id, desc: true }])
        }
    }

    const contextValue: CardsViewContextValues = {
        attributes,
        sortOptions,
        displayedAttributes,
        toggleAttribute,
        activeSorting,
        activeSortOption,
        setSortField,
        setCurrentSortToAscending,
        setCurrentSortToDescending,
    }

    return <CardsViewContext.Provider value={contextValue}>{children}</CardsViewContext.Provider>
}
